import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { MessageSquare, X, Phone, MessageCircle, Send } from "lucide-react";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_ANSWERS = {
    "Festival sales boost":
        "Perfect timing — Ganesh Chaturthi starts Sep 14, then Navratri and Diwali: the year's biggest sales window. Festival campaigns need 3–4 weeks of runway, so starting now is exactly right. A free Growth Audit maps your festival plan.",
    "Grow my sales":
        "That's exactly what ALBL is built for. We start by finding where your sales journey is leaking — visibility, enquiries, follow-up or conversion — then fix it in that order. Most businesses see the biggest jump from fixing follow-up first.",
    "Build my brand":
        "Smart move. Brand is what makes customers choose you instead of comparing prices. We build positioning, messaging and communication around your specific market — not templates.",
    "Digital marketing":
        "We do it — websites, Google and Meta campaigns, content, local SEO — but always tied to enquiries and sales, not vanity metrics. Digital is one tool in the growth plan, not the plan itself.",
    "Pricing / how it works":
        "Every engagement starts with a free Growth Audit — we look at your business, market and current marketing, and show you where growth is leaking. Then you decide. No packages forced on you.",
    "Just exploring":
        "No problem. If you run a business in Narmadapuram, Itarsi, Bhopal, Delhi, Indore or Jabalpur — the Insights section has free, practical articles written for your market.",
};

const TOPIC_ANSWERS_HI = {
    "फ़ेस्टिव सेल्स बूस्ट":
        "बिल्कुल सही समय — 14 सितंबर से गणेश चतुर्थी, फिर नवरात्रि और दिवाली: साल की सबसे बड़ी बिक्री की खिड़की। फ़ेस्टिव कैंपेन को 3–4 हफ़्ते की तैयारी चाहिए, इसलिए अभी शुरू करना सही है। मुफ़्त ग्रोथ ऑडिट से आपका फ़ेस्टिव प्लान बन जाएगा।",
    "सेल्स बढ़ानी है":
        "यही ALBL की असली काम है। हम पहले ढूंढते हैं कि आपकी सेल्स कहाँ रिस रही है — विज़िबिलिटी, enquiries, फ़ॉलो-अप या कन्वर्ज़न — फिर उसी क्रम में ठीक करते हैं। ज़्यादातर बिज़नेस को सबसे बड़ा उछाल फ़ॉलो-अप ठीक करने से ही मिलता है।",
    "ब्रांड बनाना है":
        "बढ़िया सोच। ब्रांड ही वह चीज़ है जो ग्राहक को दाम की तुलना के बजाय आपको चुनने पर मजबूर करती है। हम आपके बाज़ार के अनुसार पोज़िशनिंग, मैसेजिंग और कम्युनिकेशन बनाते हैं — टेम्पलेट नहीं।",
    "डिजिटल मार्केटिंग":
        "हम करते हैं — वेबसाइट, Google और Meta कैंपेन, कंटेंट, लोकल SEO — लेकिन हमेशा enquiries और सेल्स से जोड़कर, दिखावटी आंकड़ों से नहीं। डिजिटल ग्रोथ प्लान का एक औज़ार है, प्लान ख़ुद नहीं।",
    "प्राइसिंग / तरीका":
        "हर काम की शुरुआत मुफ़्त ग्रोथ ऑडिट से होती है — हम आपके बिज़नेस, बाज़ार और मौजूदा मार्केटिंग को देखते हैं और दिखाते हैं कि ग्रोथ कहाँ रिस रही है। फिर आप फ़ैसला करते हैं। कोई पैकेज ज़बरदस्ती नहीं।",
    "बस देख रहा हूँ":
        "कोई बात नहीं। अगर आपका बिज़नेस नर्मदापुरम, इतारसी, भोपाल, दिल्ली, इंदौर या जबलपुर में है — Insights सेक्शन में आपके बाज़ार के लिए लिखे मुफ़्त, व्यावहारिक लेख हैं।",
};

const CITIES = ["Narmadapuram", "Itarsi", "Bhopal", "Delhi", "Indore", "Jabalpur", "Other"];

const EN = {
    cta: "Ask ALBL",
    header: "ALBL Assistant",
    headerSub: "Replies instantly · Humans on WhatsApp",
    greeting: "Hi, I'm the ALBL assistant. I can point you in the right direction in under a minute. What brings you here?",
    answers: TOPIC_ANSWERS,
    actions: { audit: "Get a Growth Audit", callback: "Get a callback", whatsapp: "Chat on WhatsApp" },
    auditMsg: "Perfect — the audit form takes 30 seconds. Tap below, or keep chatting here.",
    auditLink: "Open the Growth Audit form →",
    waMsg: "You'll talk directly with the ALBL team — fastest way to reach us.",
    askName: "Done — we'll call you. First, your name?",
    askPhone: (n) => `Nice to meet you, ${n}. Best phone number for the callback?`,
    askCity: "Got it. Which market is your business in?",
    sending: "Sending…",
    success: (l) => `Thanks ${l.name}! Your details are with ALBL — expect a call on ${l.phone}. For anything urgent, WhatsApp us right now:`,
    fail: "Hmm, that didn't go through. Please tap below to reach us on WhatsApp directly:",
    typing: "typing…",
    namePh: "Your name",
    phonePh: "Your phone number",
    call: "Prefer to talk? Call",
    contactPath: "/contact?intent=audit",
    cities: CITIES.map((c) => ({ label: c, value: c })),
};

const HI = {
    cta: "ALBL से पूछें",
    header: "ALBL असिस्टेंट",
    headerSub: "तुरंत जवाब · WhatsApp पर टीम",
    greeting: "नमस्ते! मैं ALBL असिस्टेंट हूँ। एक मिनट में आपको सही दिशा दिखा दूँगा — आप क्या चाहते हैं?",
    answers: TOPIC_ANSWERS_HI,
    actions: { audit: "ग्रोथ ऑडिट लें", callback: "कॉलबैक चाहिए", whatsapp: "WhatsApp पर बात करें" },
    auditMsg: "बहुत बढ़िया — ऑडिट फ़ॉर्म में सिर्फ़ 30 सेकंड लगते हैं। नीचे टैप करें, या यहीं चैट जारी रखें।",
    auditLink: "ग्रोथ ऑडिट फ़ॉर्म खोलें →",
    waMsg: "आप सीधे ALBL टीम से बात करेंगे — सबसे तेज़ तरीक़ा।",
    askName: "पक्का — हम कॉल करेंगे। पहले आपका नाम?",
    askPhone: (n) => `मिलकर अच्छा लगा, ${n}। कॉलबैक के लिए बेस्ट नंबर?`,
    askCity: "लिया गया। आपका बिज़नेस किस बाज़ार में है?",
    sending: "भेजा जा रहा है…",
    success: (l) => `धन्यवाद ${l.name}! आपकी details ALBL के पास हैं — ${l.phone} पर कॉल की उम्मीद रखिए। ज़रूरी हो तो अभी WhatsApp कीजिए:`,
    fail: "अभी भेजा नहीं जा सका। कृपया नीचे टैप करके सीधे WhatsApp कीजिए:",
    typing: "लिखा जा रहा है…",
    namePh: "आपका नाम",
    phonePh: "आपका फ़ोन नंबर",
    call: "बात करनी है? कॉल करें",
    contactPath: "/hi/contact?intent=audit",
    cities: [
        { label: "नर्मदापुरम", value: "Narmadapuram" },
        { label: "इतारसी", value: "Itarsi" },
        { label: "भोपाल", value: "Bhopal" },
        { label: "दिल्ली", value: "Delhi" },
        { label: "इंदौर", value: "Indore" },
        { label: "जबलपुर", value: "Jabalpur" },
        { label: "अन्य", value: "Other" },
    ],
};

const Chatbot = () => {
    const { pathname } = useLocation();
    const L = pathname.startsWith("/hi") ? HI : EN;
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [options, setOptions] = useState([]);
    const [inputMode, setInputMode] = useState("none");
    const [input, setInput] = useState("");
    const [lead, setLead] = useState({ topic: "", name: "", phone: "", city: "" });
    const [sending, setSending] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (open && messages.length === 0) {
            botSay(L.greeting, Object.keys(L.answers));
        }
    }, [open]);

    useEffect(() => {
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, options]);

    const botSay = (text, opts = [], mode = "none") => {
        setSending(true);
        setOptions([]);
        setInputMode("none");
        setTimeout(() => {
            setMessages((m) => [...m, { from: "bot", text }]);
            setOptions(opts);
            setInputMode(mode);
            setSending(false);
        }, 550);
    };

    const userSay = (text) => setMessages((m) => [...m, { from: "user", text }]);

    const handleTopic = (topic) => {
        userSay(topic);
        setLead((l) => ({ ...l, topic }));
        botSay(L.answers[topic], [L.actions.audit, L.actions.callback, L.actions.whatsapp]);
    };

    const handleAction = (action) => {
        userSay(action);
        if (action === L.actions.audit) {
            botSay(L.auditMsg, [], "none");
            setTimeout(() => setOptions(["__AUDIT_LINK__", L.actions.callback]), 600);
        } else if (action === L.actions.whatsapp) {
            botSay(L.waMsg, ["__WA_LINK__", L.actions.callback]);
        } else if (action === L.actions.callback) {
            botSay(L.askName, [], "text-name");
        }
    };

    const handleCity = (label) => {
        userSay(label);
        const value = (L.cities.find((c) => c.label === label) || {}).value || label;
        const finalLead = { ...lead, city: value };
        setLead(finalLead);
        submitLead(finalLead);
    };

    const submitLead = async (l) => {
        botSay(L.sending, [], "none");
        try {
            await axios.post(`${API}/contact`, {
                name: l.name,
                phone: l.phone,
                city: l.city,
                interest: `Chatbot — ${l.topic || "General"}`,
                message: `Callback requested via website chatbot. Topic: ${l.topic || "General"}.`,
            });
            setTimeout(() => {
                setMessages((m) => [...m, { from: "bot", text: L.success(l) }]);
                setOptions(["__WA_LINK__"]);
            }, 700);
        } catch (e) {
            setTimeout(() => {
                setMessages((m) => [...m, { from: "bot", text: L.fail }]);
                setOptions(["__WA_LINK__"]);
            }, 700);
        }
    };

    const handleSend = () => {
        const val = input.trim();
        if (!val) return;
        setInput("");
        userSay(val);
        if (inputMode === "text-name") {
            setLead((l) => ({ ...l, name: val }));
            botSay(L.askPhone(val), [], "text-phone");
        } else if (inputMode === "text-phone") {
            setLead((l) => ({ ...l, phone: val }));
            botSay(L.askCity, L.cities.map((c) => c.label), "none");
        }
    };

    const renderOption = (opt) => {
        if (opt === "__AUDIT_LINK__")
            return (
                <Link
                    key={opt}
                    to={L.contactPath}
                    onClick={() => setOpen(false)}
                    data-testid="chatbot-audit-link"
                    className="text-left text-xs font-bold tracking-wide bg-[#0D131F] text-white px-4 py-3 hover:bg-[#1E3A8A] transition-colors"
                >
                    {L.auditLink}
                </Link>
            );
        if (opt === "__WA_LINK__")
            return (
                <a
                    key={opt}
                    href={SITE.whatsappPrefill}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="chatbot-whatsapp-link"
                    className="text-left text-xs font-bold tracking-wide bg-[#25D366] text-[#0D131F] px-4 py-3 flex items-center gap-2"
                >
                    <MessageCircle className="h-4 w-4" /> WhatsApp — {SITE.phone}
                </a>
            );
        const handler = L.answers[opt] !== undefined ? handleTopic : L.cities.some((c) => c.label === opt) ? handleCity : handleAction;
        return (
            <button
                key={opt}
                onClick={() => handler(opt)}
                data-testid={`chatbot-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-left text-xs font-semibold tracking-wide border border-slate-300 bg-white text-slate-800 px-4 py-3 hover:border-[#0D131F] hover:bg-[#0D131F] hover:text-white transition-colors"
            >
                {opt}
            </button>
        );
    };

    return (
        <>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="chatbot-open-button"
                    aria-label="Open ALBL chat assistant"
                    className="fixed bottom-5 left-5 z-40 flex items-center gap-2 bg-[#0D131F] text-white border border-[#C5A059]/50 rounded-full shadow-xl shadow-black/25 px-4 py-3 sm:px-5 sm:py-3.5 btn-sharp"
                >
                    <MessageSquare className="h-5 w-5 text-[#C5A059]" />
                    <span className="text-xs font-bold tracking-[0.1em] uppercase">{L.cta}</span>
                </button>
            )}

            {open && (
                <div
                    data-testid="chatbot-panel"
                    className="fixed bottom-5 left-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm h-[480px] max-h-[75vh] bg-white border border-slate-200 shadow-2xl shadow-black/30 flex flex-col"
                >
                    <div className="bg-[#0D131F] text-white px-5 py-4 flex items-center justify-between shrink-0">
                        <div>
                            <p className="font-display font-bold text-sm tracking-wide">{L.header}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">{L.headerSub}</p>
                        </div>
                        <button onClick={() => setOpen(false)} data-testid="chatbot-close-button" aria-label="Close chat" className="p-1.5 hover:text-[#C5A059] transition-colors">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F8FAFC]">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                                        m.from === "user"
                                            ? "bg-[#1E3A8A] text-white"
                                            : "bg-white border border-slate-200 text-slate-800"
                                    }`}
                                >
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-slate-200 px-4 py-3 text-sm text-slate-400 italic">
                                    {L.typing}
                                </div>
                            </div>
                        )}
                    </div>

                    {options.length > 0 && (
                        <div className="px-4 py-3 grid gap-2 border-t border-slate-100 bg-white shrink-0 max-h-44 overflow-y-auto">
                            {options.map(renderOption)}
                        </div>
                    )}

                    {inputMode !== "none" && (
                        <div className="px-4 py-3 border-t border-slate-100 bg-white flex gap-2 shrink-0">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                placeholder={inputMode === "text-name" ? L.namePh : L.phonePh}
                                data-testid="chatbot-input"
                                className="flex-1 border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#0D131F]"
                            />
                            <button
                                onClick={handleSend}
                                data-testid="chatbot-send-button"
                                aria-label="Send"
                                className="bg-[#0D131F] text-white px-4 flex items-center hover:bg-[#1E3A8A] transition-colors"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <a
                        href={SITE.phoneHref}
                        data-testid="chatbot-call-link"
                        className="px-4 py-2.5 border-t border-slate-100 bg-white text-[11px] text-slate-500 flex items-center gap-2 hover:text-[#0D131F] transition-colors shrink-0"
                    >
                        <Phone className="h-3.5 w-3.5 text-[#C5A059]" /> {L.call} {SITE.phone}
                    </a>
                </div>
            )}
        </>
    );
};

export default Chatbot;
